import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import { ensureCsrfToken } from '../utils/csrf';
import { setTokens, getRefreshToken, clearTokens, getAccessToken } from '../utils/tokenStorage';
import type {
  AuthResponseDTO,
  AuthUserDTO,
  RefreshResponseDTO
} from '../types/auth';

type User = AuthUserDTO;

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

type AuthSuccessResponse = ApiResponse<AuthResponseDTO>;
type RefreshSuccessResponse = ApiResponse<RefreshResponseDTO>;
type UserProfileResponse = ApiResponse<{ user: AuthUserDTO }>;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (username: string, email: string, password: string, country: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Configure axios to send credentials (cookies) with all requests
  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  // Request interceptor - attach Authorization from tokenStorage, and CSRF for mutating requests
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        config.headers = config.headers || {};

        // Always attach Authorization from tokenStorage
        const accessToken = getAccessToken();
        if (accessToken) {
          (config.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
        }

        try {
          const method = (config.method || 'get').toLowerCase();
          const requiresCsrf = ['post', 'put', 'patch', 'delete'].includes(method);

          if (requiresCsrf) {
            const token = await ensureCsrfToken();
            if (token) {
              (config.headers as Record<string, string>)['X-CSRF-Token'] = token;
            }
          }
        } catch (csrfError) {
          console.warn('Failed to attach CSRF token to request:', csrfError);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  const profileFetchInFlight = useRef(false);
  const refreshInProgress = useRef<Promise<boolean> | null>(null);

  // Function to fetch and update user profile
  const refreshProfile = useCallback(async () => {
    if (profileFetchInFlight.current) return;
    profileFetchInFlight.current = true;
    try {
      const response = await axios.get<UserProfileResponse>('/api/user/profile');
      const payload = response.data;

      if (payload.success && payload.data?.user) {
        const newUser = payload.data.user;

        // Ensure isAdmin is a boolean
        const normalizedUser = {
          ...newUser,
          isAdmin: Boolean(newUser.isAdmin)
        };

        setUser(normalizedUser);
      } else {
        console.warn('[AuthContext] Profile response missing user data:', {
          success: payload.success,
          hasData: !!payload.data,
          hasUser: !!payload.data?.user,
          payload: JSON.stringify(payload, null, 2)
        });
        if (!getAccessToken()) setUser(null);
      }
    } catch (error: any) {
      // User is not authenticated or token expired
      const isUnauthorized = error?.response?.status === 401;
      if (!isUnauthorized) {
        console.warn('[AuthContext] Failed to fetch profile:', error);
      }
      // Don't clear user if we have tokens - a stale 401 (e.g. from initial checkAuth
      // before login) could overwrite a successful login
      if (!getAccessToken()) {
        setUser(null);
      }
    } finally {
      profileFetchInFlight.current = false;
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      await refreshProfile();
      setLoading(false);
    };

    checkAuth();
  }, [refreshProfile]);

  // Set up axios interceptor for token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const originalRequest = error.config;
          const requestUrl =
            typeof originalRequest?.url === 'string' ? originalRequest.url : '';

          // Never attempt refresh/retry for auth endpoints (prevents infinite login loops)
          const isAuthEndpoint =
            requestUrl.includes('/api/auth/login') ||
            requestUrl.includes('/api/auth/register') ||
            requestUrl.includes('/api/auth/logout') ||
            requestUrl.includes('/api/auth/csrf-token') ||
            requestUrl.includes('/api/auth/refresh') ||
            requestUrl.includes('/api/auth/google') ||
            requestUrl.includes('/api/auth/apple');

          if (isAuthEndpoint) {
            return Promise.reject(error);
          }

          // Skip token refresh for public routes when user is not authenticated
          const isPublicRoute =
            requestUrl.includes('/api/leaderboard') ||
            requestUrl.includes('/api/quiz/questions') ||
            requestUrl.includes('/api/quiz/submit-guest');

          if (isPublicRoute) {
            return Promise.reject(error);
          }

          // Only retry a given request once to prevent request storms
          if (originalRequest && (originalRequest as any)._retry) {
            return Promise.reject(error);
          }
          if (originalRequest) {
            (originalRequest as any)._retry = true;
          }

          const storedRefreshToken = getRefreshToken();
          if (!storedRefreshToken) {
            logout();
            return Promise.reject(error);
          }

          const doRefresh = async (): Promise<boolean> => {
            try {
              const response = await axios.post<RefreshSuccessResponse>(
                '/api/auth/refresh',
                { refreshToken: storedRefreshToken! }
              );
              const payload = response.data;
              if (payload.success && payload.data) {
                const {
                  geoRegion: refreshedRegion,
                  country: refreshedCountry,
                  countryCode: refreshedCountryCode,
                  isPremium: refreshedPremium,
                  isAdmin: refreshedAdmin,
                  accessToken: newAccessToken,
                  refreshToken: newRefreshToken
                } = payload.data;

                if (newAccessToken && newRefreshToken) {
                  setTokens(newAccessToken, newRefreshToken);
                  axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                }

                setUser((prev: User | null) => {
                  if (!prev) return prev;
                  const newIsAdmin = typeof refreshedAdmin === 'boolean' ? refreshedAdmin : prev.isAdmin;
                  return {
                    ...prev,
                    geoRegion: refreshedRegion || prev.geoRegion,
                    country: refreshedCountry !== undefined ? refreshedCountry : prev.country,
                    countryCode: refreshedCountryCode !== undefined ? refreshedCountryCode : prev.countryCode,
                    isPremium: typeof refreshedPremium === 'boolean' ? refreshedPremium : prev.isPremium,
                    isAdmin: newIsAdmin
                  };
                });

                try {
                  await refreshProfile();
                } catch {
                  // Non-fatal
                }
                return true;
              }
            } catch {
              logout();
              return false;
            }
            return false;
          };

          // Serialize refresh: only one refresh at a time; others wait and retry
          let refreshPromise = refreshInProgress.current;
          if (!refreshPromise) {
            refreshPromise = doRefresh();
            refreshInProgress.current = refreshPromise;
          }
          refreshPromise.finally(() => {
            if (refreshInProgress.current === refreshPromise) {
              refreshInProgress.current = null;
            }
          });

          const refreshed = await refreshPromise;
          if (refreshed && originalRequest) {
            return axios.request(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout, refreshProfile]);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await axios.post<AuthSuccessResponse>(
        '/api/auth/login', 
        { email, password, rememberMe },
        { withCredentials: true }
      );
      const payload = response.data;
      
      if (payload.success && payload.data) {
        const { user: userData, accessToken: at, refreshToken: rt } = payload.data;

        // Cross-origin auth requires tokens in response; cookies are blocked
        if (!at || !rt) {
          throw new Error('Login failed: authentication tokens not received. Please try again.');
        }
        setTokens(at, rt);
        axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;

        // Set user from login response - skip refreshProfile to avoid its catch clearing user on 401
        const normalizedUser = userData
          ? { ...userData, isAdmin: Boolean(userData.isAdmin) }
          : null;
        if (normalizedUser) setUser(normalizedUser);
      } else {
        throw new Error(payload.message || 'Login failed');
      }
    } catch (error: any) {
      // Handle CSRF token errors - retry once after forcing a fresh token
      const status = error.response?.status;
      const code = error.response?.data?.code;
      const errorMessage: string | undefined =
        error.response?.data?.error || error.response?.data?.message;

      const isCsrfError =
        status === 403 &&
        (code === 'CSRF_TOKEN_MISSING' ||
          code === 'CSRF_TOKEN_INVALID' ||
          code === 'CSRF_TOKEN_MISMATCH' ||
          (typeof errorMessage === 'string' &&
            errorMessage.toUpperCase().includes('CSRF')));

      if (isCsrfError) {
        try {
          // Force-refresh CSRF token and retry login once
          await ensureCsrfToken(true);

          const retryResponse = await axios.post<AuthSuccessResponse>(
            '/api/auth/login',
            { email, password, rememberMe },
            { withCredentials: true }
          );
          const retryPayload = retryResponse.data;

          if (retryPayload.success && retryPayload.data) {
            const { user: retryUser, accessToken: at, refreshToken: rt } = retryPayload.data;
            if (!at || !rt) throw new Error('Login failed: authentication tokens not received.');
            setTokens(at, rt);
            axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;
            const normalized = retryUser ? { ...retryUser, isAdmin: Boolean(retryUser.isAdmin) } : null;
            if (normalized) setUser(normalized);
            return;
          }
        } catch (retryError: any) {
          const retryMessage: string | undefined =
            retryError.response?.data?.error || retryError.response?.data?.message;
          if (retryMessage) {
            throw new Error(retryMessage);
          }
        }
      }

      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  };

  const register = async (username: string, email: string, password: string, country: string) => {
    try {
      const response = await axios.post<AuthSuccessResponse>('/api/auth/register', { 
        username, 
        email, 
        password, 
        country 
      });
      const payload = response.data;
      
      if (payload.success && payload.data) {
        const { user: userData, accessToken: at, refreshToken: rt } = payload.data;
        if (at && rt) {
          setTokens(at, rt);
          axios.defaults.headers.common['Authorization'] = `Bearer ${at}`;
        }
        setUser(userData);
      } else {
        throw new Error(payload.message || 'Registration failed');
      }
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Registration failed. Please try again.');
    }
  };

  const refreshToken = async () => {
    try {
      const storedRefreshToken = getRefreshToken();
      const response = await axios.post<RefreshSuccessResponse>(
        '/api/auth/refresh',
        storedRefreshToken ? { refreshToken: storedRefreshToken } : {}
      );
      const payload = response.data;
      if (payload.success && payload.data) {
        const {
          geoRegion: refreshedRegion,
          country: refreshedCountry,
          countryCode: refreshedCountryCode,
          isPremium: refreshedPremium,
          isAdmin: refreshedAdmin,
          accessToken: newAccessToken,
          refreshToken: newRefreshToken
        } = payload.data;
        if (newAccessToken && newRefreshToken) {
          setTokens(newAccessToken, newRefreshToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        }
        setUser((prev: User | null) => {
          if (!prev) {
            return prev;
          }
          const wasAdmin = prev.isAdmin === true;
          const newIsAdmin = typeof refreshedAdmin === 'boolean' ? refreshedAdmin : prev.isAdmin;
          const isAdminChanged = wasAdmin !== (newIsAdmin === true);
          
          // Log isAdmin changes for debugging
          if (isAdminChanged) {
            console.warn('[AuthContext] isAdmin changed during token refresh:', {
              userId: prev.id,
              username: prev.username,
              wasAdmin,
              newIsAdmin,
              refreshedAdminValue: refreshedAdmin,
              refreshedAdminType: typeof refreshedAdmin
            });
          }
          
          // Defensive check: warn if admin status was lost
          if (wasAdmin && newIsAdmin !== true) {
            console.error('[AuthContext] WARNING: Admin status lost during token refresh!', {
              userId: prev.id,
              username: prev.username,
              previousIsAdmin: prev.isAdmin,
              newIsAdmin,
              refreshedAdminValue: refreshedAdmin
            });
          }
          
          return {
            ...prev,
            geoRegion: refreshedRegion || prev.geoRegion,
            country: refreshedCountry !== undefined ? refreshedCountry : prev.country,
            countryCode: refreshedCountryCode !== undefined ? refreshedCountryCode : prev.countryCode,
            isPremium: typeof refreshedPremium === 'boolean' ? refreshedPremium : prev.isPremium,
            isAdmin: newIsAdmin
          };
        });
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      logout();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
